//#region Imports

import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FileUploadDropzoneComponent } from '../../../../../shared/components/file-upload-dropzone/file-upload-dropzone.component';
import { FileUploadStatus } from '../../../../../shared/components/file-upload-dropzone/file-upload-status.type';
import { ToastService } from '../../../../../shared/components/toast/service/toast.service';
import { formatFileSizeMb } from '../../../../../shared/helpers/file-size.helper';
import { ClassForm } from '../../../../../shared/models/interfaces/forms/course-form.interface';

//#endregion

const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
const DOCUMENT_EXTENSIONS = ['.zip', '.pdf'];

@Component({
  selector: 'l-course-content-type-web-page',
  standalone: true,
  templateUrl: './course-content-type-web-page.component.html',
  imports: [FileUploadDropzoneComponent],
})
export class CourseContentTypeWebPageComponent {

  //#region Inputs

  @Input({ required: true })
  public form!: FormGroup<ClassForm>;

  @Input()
  public file: File | null = null;

  @Input()
  public fileSizeLabel: string = '';

  //#endregion

  //#region Outputs

  @Output()
  public readonly fileStateChange = new EventEmitter<{ file: File | null; fileSizeLabel: string }>();

  //#endregion

  //#region Public Properties

  public readonly webPageFileInputId = 'course-content-web-page-file';

  public displayFileName: string = '';

  //#endregion

  //#region Private Properties

  private readonly toastService = inject(ToastService);

  //#endregion

  //#region Getters

  public get uploadStatus(): FileUploadStatus {
    return this.displayFileName || this.form.controls.webPageFileName.value ? 'uploaded' : 'idle';
  }

  public get uploadedTitle(): string {
    return isHttpUrl(this.form.controls.webPageFileName.value) ? 'Arquivo enviado!' : 'Arquivo selecionado';
  }

  public get shownFileName(): string {
    return this.displayFileName || storedFileLabel(this.form.controls.webPageFileName.value);
  }

  //#endregion

  //#region Public Methods

  public onWebPageFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const selectedFile = input?.files?.[0] ?? null;

    if (!selectedFile) {
      this.resetSelection();
      return;
    }

    if (!hasExtension(selectedFile.name, DOCUMENT_EXTENSIONS)) {
      this.toastService.error('Formatos aceitos: .zip e .pdf.');
      this.clearInput(input);
      this.resetSelection();
      return;
    }

    if (selectedFile.size > MAX_DOCUMENT_BYTES) {
      this.toastService.error('Arquivo muito grande. Tamanho máximo: 10MB');
      this.clearInput(input);
      this.resetSelection();
      return;
    }

    this.displayFileName = selectedFile.name;
    this.fileStateChange.emit({
      file: selectedFile,
      fileSizeLabel: formatFileSizeMb(selectedFile.size),
    });
  }

  public onDeleteFile(): void {
    this.resetSelection();
  }

  //#endregion

  //#region Private Methods

  private resetSelection(): void {
    this.displayFileName = '';
    this.fileStateChange.emit({ file: null, fileSizeLabel: '' });
  }

  private clearInput(input: HTMLInputElement | null): void {
    if (input) {
      input.value = '';
    }
  }

  //#endregion

}

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function storedFileLabel(value: string): string {
  const trimmed = value.trim();

  if (!trimmed) {
    return '';
  }

  try {
    const segment = new URL(trimmed).pathname.split('/').findLast((part) => part.length > 0) ?? trimmed;
    return decodeURIComponent(segment);
  } catch {
    return trimmed;
  }
}

function hasExtension(fileName: string, extensions: string[]): boolean {
  const lowerName = fileName.toLowerCase();
  return extensions.some((extension) => lowerName.endsWith(extension));
}
